import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import QRScanStats from './QRScanStats';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';
import { previousScanMessage } from '../utils/scanHistory';
import { AlertCircle, CheckCircle } from 'lucide-react';
import FaceCapture from './FaceCapture';
import QrScanner from 'qr-scanner';

// Configure QR scanner worker path
QrScanner.WORKER_PATH = '/static/js/qr-scanner-worker.min.js';

// Alternative: Use CDN worker if local fails
// QrScanner.WORKER_PATH = 'https://unpkg.com/qr-scanner@1.4.2/qr-scanner-worker.min.js';

const QRScanner = ({ 
  onScan, 
  onError, 
  eventId,
  purposes = [],
  preselectedPurpose = null,
  prefillRegistrationId = null,
  continuousMode = false,
  // When true, even "continuous" purposes (no edit / no badge) will stop camera after a successful scan.
  // This is useful on routes where the operator wants to manually re-start scanning each time.
  autoStopAfterSuccess = false,
  // Controls whether face recognition mode is available (from event.faceIdEnabled)
  faceIdEnabled = true,
  // When provided, the parent owns purpose selection and stats; the scanner hides its own.
  selectedPurpose: controlledPurpose,
  // When provided ('camera' | 'face'), the parent owns the scan method and its own reader/search input.
  scanMethod: controlledMethod,
  className = "p-4" 
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [internalPurpose, setSelectedPurpose] = useState(preselectedPurpose);
  const isPurposeControlled = controlledPurpose !== undefined;
  const selectedPurpose = isPurposeControlled ? controlledPurpose : internalPurpose;
  const [scannedData, setScannedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [manualQRCode, setManualQRCode] = useState('');
  const [internalMethod, setScanMethod] = useState('reader'); // 'reader', 'camera', 'face'
  const isMethodControlled = controlledMethod !== undefined;
  const scanMethod = isMethodControlled ? controlledMethod : internalMethod;
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [isFaceScanning, setIsFaceScanning] = useState(false);
  const manualInputRef = useRef(null);
  const processQrRef = useRef(null);
  // Last purpose config that still had an id. A successful scan used to mutate the
  // shared list and blank this out; keep a copy so the next scan can still resolve it.
  const purposeConfigRef = useRef(null);

  const activePurposeId = selectedPurpose || preselectedPurpose || null;
  const matchedPurpose = purposes.find((p) => p?.purpose === activePurposeId);
  if (matchedPurpose) {
    purposeConfigRef.current = { ...matchedPurpose };
  }

  // Update selectedPurpose when preselectedPurpose changes
  useEffect(() => {
    if (preselectedPurpose && purposes.some(p => p.purpose === preselectedPurpose)) {
      setSelectedPurpose(preselectedPurpose);
    }
  }, [preselectedPurpose, purposes]);

  // Auto-select purpose if there's only one available
  useEffect(() => {
    if (purposes.length === 1 && !selectedPurpose) {
      setSelectedPurpose(purposes[0].purpose);
    }
  }, [purposes, selectedPurpose]);

  // Prefill manual entry field if registration ID is provided and auto-submit
  useEffect(() => {
    if (prefillRegistrationId && !manualQRCode && !isLoading && !scanResult && !isAutoSubmitting) {
  
      // Set the manual QR code and auto-submit state
      setManualQRCode(prefillRegistrationId);
      setIsAutoSubmitting(true);
      
      // Auto-submit after a short delay to ensure state is updated
      setTimeout(async () => {
        try {
          // Create placeholder image for manual entry
          const placeholderImage = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k=';
          
          // Process the registration ID automatically
          await captureAndProcessQR(prefillRegistrationId.trim(), placeholderImage);
        } catch (error) {
          console.error('Auto-submit failed:', error);
        } finally {
          setIsAutoSubmitting(false);
        }
      }, 1000); // Increased delay to ensure component is ready
    }
  }, [prefillRegistrationId, manualQRCode, isLoading, scanResult, isAutoSubmitting]);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!isMethodControlled && !faceIdEnabled && scanMethod === 'face') {
      setScanMethod('reader');
      setIsFaceScanning(false);
    }
  }, [faceIdEnabled, scanMethod, isMethodControlled]);

  // Enhanced QR scanner with real QR code detection
  const startScanning = async () => {
    try {
      setIsScanning(true);
      setScanResult(null);
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      onError?.('Failed to start QR scanner.');
      setIsScanning(false);
    }
  };

  // Initialize QR scanner when video element is available
  useEffect(() => {
    if (isScanning && videoRef.current && !qrScannerRef.current) {
      const initializeScanner = async () => {
        try {
          // Small delay to ensure video element is fully rendered
          await new Promise(resolve => setTimeout(resolve, 100));
          
          if (!videoRef.current) {
            throw new Error('Video element not available after delay');
          }

          // Create QR scanner instance
          const qrScanner = new QrScanner(
            videoRef.current,
            (result) => {
              // QR code detected - capture image and send to backend.
              // Call through a ref so a long-lived camera callback sees the current purpose.
              processQrRef.current?.(result.data);
            },
            {
              highlightScanRegion: true,
              highlightCodeOutline: true,
              preferredCamera: 'environment',
            }
          );

          qrScannerRef.current = qrScanner;
          
          // Start scanning
          await qrScanner.start();
          
        } catch (error) {
          console.error('Error initializing QR scanner:', error);
          
          // Handle specific worker loading errors
          if (error.message && error.message.includes('worker')) {
            onError?.('QR scanner worker failed to load. Please refresh the page and try again.');
          } else if (error.name === 'NotAllowedError') {
            onError?.('Camera access denied. Please allow camera access to scan QR codes.');
          } else if (error.name === 'NotFoundError') {
            onError?.('No camera found. Please ensure a camera is connected.');
          } else {
            onError?.('Camera access denied or QR scanner failed to start. Please allow camera access to scan QR codes.');
          }
          
          setIsScanning(false);
        }
      };

      initializeScanner();
    }
  }, [isScanning]);

  const stopScanning = () => {
    setIsScanning(false);
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
  };

  // Capture image from video and convert to base64
  const captureImageAsBase64 = () => {
    if (!videoRef.current || !canvasRef.current) {
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  const resolvePurposeConfig = () => {
    const purposeId = selectedPurpose || preselectedPurpose || null;
    const fromList = purposes.find((p) => p?.purpose === purposeId);
    if (fromList) return { ...fromList };
    if (purposeConfigRef.current?.purpose === purposeId) return purposeConfigRef.current;
    return null;
  };

  // Process QR code detection result
  const captureAndProcessQR = async (qrData, providedImage = null) => {
    setIsLoading(true);
    setScannedData(qrData);
    
    try {
      // Get the purpose configuration
      const purposeConfig = resolvePurposeConfig();
      if (!purposeConfig) {
        throw new Error('Please select a scan purpose');
      }

      // Check inventory if needed
      if (purposeConfig.hasInventory && purposeConfig.remainingInventory === 0) {
        throw new Error('No inventory available for this purpose');
      }

      // Check if in continuous mode (no edit, no badge)
      const isInContinuousMode = !purposeConfig.canUpdateUser && !purposeConfig.shouldPrintBadge;
      
      // Stop scanning immediately after the first detection when requested,
      // otherwise stop only for non-continuous purposes.
      if (autoStopAfterSuccess || !isInContinuousMode) {
        stopScanning();
      }
      
      // Use provided image or capture the current video frame as base64
      const base64Image = providedImage || captureImageAsBase64();
      
      if (!base64Image) {
        throw new Error('Failed to capture image');
      }

      // Send to backend with base64 image data and purpose configuration
      const result = await ApiService.scanQR(eventId, { 
        qrCode: base64Image, 
        purpose: selectedPurpose,
        qrData: qrData,
        scanMode: 'qr',
        shouldPrintBadge: purposeConfig.shouldPrintBadge,
        canUpdateUser: purposeConfig.canUpdateUser
      });

      // Add purpose configuration to the result
      const purposeConfigData = {
        shouldPrintBadge: purposeConfig.shouldPrintBadge,
        canUpdateUser: purposeConfig.canUpdateUser,
        name: purposeConfig.name,
        purpose: purposeConfig.purpose,
        purposes: purposeConfig.purpose,
      };
      
      result.purposeConfig = purposeConfigData;
      
      if (result.error) {
        // If user not found, show error
        const errorResult = {
          error: result.error,
          message: result.message || 'User not found',
          userId: qrData,
          scanStatus: 'error',
          purposeConfig: purposeConfigData, // Include purpose config for error handling
          previousScanCount: result.previousScanCount,
          lastScannedAt: result.lastScannedAt,
        };
        setScanResult(errorResult);
        onScan?.(errorResult);

        // When configured, also stop scanning on error responses from the scan API.
        if (autoStopAfterSuccess) {
          stopScanning();
        }
      } else {
        const successResult = {
          ...result,
          scanStatus: 'success',
          purpose: purposeConfig.name
        };
        setScanResult(successResult);
        onScan?.(successResult);

        // If this route wants manual re-start between scans, also stop after success
        // (including continuous-mode purposes).
        if (autoStopAfterSuccess) {
          stopScanning();
        }
      }
    } catch (error) {
      console.error('Error processing QR scan:', error);
      
      // Try to get purpose config for error handling
      const purposeConfig = purposes.find(p => p.purpose === selectedPurpose);
      const errorResult = {
        error: true,
        message: error.message || 'Failed to process QR code',
        userId: qrData,
        purpose: selectedPurpose,
        scanStatus: 'error',
        purposeConfig: purposeConfig ? {
          shouldPrintBadge: purposeConfig.shouldPrintBadge,
          canUpdateUser: purposeConfig.canUpdateUser,
          name: purposeConfig.name,
          purpose: purposeConfig.purpose,
          purposes: purposeConfig.purpose
        } : null
      };
      setScanResult(errorResult);
      onScan?.(errorResult); // Call onScan with error result instead of onError

      // Also stop scanning on unexpected errors when configured.
      if (autoStopAfterSuccess) {
        stopScanning();
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleFaceCapture = async (imageSrc) => {
    // Once we have a face image for this cycle, stop the live scanner.
    // The operator can click "Start Face Scan" again for the next person,
    // mirroring the QR scan flow behavior.
    setIsFaceScanning(false);
    setIsLoading(true);
    try {
      // Get the purpose configuration
      const purposeConfig = resolvePurposeConfig();
      if (!purposeConfig) {
        throw new Error('Please select a scan purpose');
      }

      // Check inventory if needed
      if (purposeConfig.hasInventory && purposeConfig.remainingInventory === 0) {
        throw new Error('No inventory available for this purpose');
      }

      // Send face image to backend for recognition
      const result = await ApiService.scanQR(eventId, {
        liveImageBase64: imageSrc,
        purpose: selectedPurpose,
        scanMode: 'face',
        shouldPrintBadge: purposeConfig.shouldPrintBadge,
        canUpdateUser: purposeConfig.canUpdateUser
      });

      if (result.error) {
        const errorResult = {
          error: result.error,
          message: result.message || 'Face not recognized',
          scanStatus: 'error'
        };
        setScanResult(errorResult);
        onError?.(result.message || 'Face not recognized');
      } else {
        // Add purpose configuration to the result
        const successResult = {
          ...result,
          scanStatus: 'success',
          purpose: purposeConfig.name,
          purposeConfig: {
            shouldPrintBadge: purposeConfig.shouldPrintBadge,
            canUpdateUser: purposeConfig.canUpdateUser,
            name: purposeConfig.name,
            purpose: purposeConfig.purpose,
            purposes: purposeConfig.purpose
          }
        };
        setScanResult(successResult);
        onScan?.(successResult);
      }
    } catch (error) {
      console.error('Face recognition error:', error);
      onError?.(error.message || 'Failed to process face image');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFaceError = (error) => {
    onError?.(error.message || 'Failed to capture face image');
  };

  const handleManualEntrySubmit = (e) => {
    e?.preventDefault();
    if (manualQRCode.trim()) {
      // For manual entry, we'll create a simple base64 image placeholder
      const placeholderImage = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k=';
      
      // Process the manual QR code
      captureAndProcessQR(manualQRCode.trim(), placeholderImage);
      
      // Reset input
      setManualQRCode('');
      
      // Keep focus on input for next scan
      setTimeout(() => {
        if (manualInputRef.current) {
          manualInputRef.current.focus();
        }
      }, 100);
    }
  };

  processQrRef.current = captureAndProcessQR;

  const handleScanMethodChange = (method) => {
    stopScanning();
    setIsFaceScanning(false);
    setScanResult(null);
    setScanMethod(method);
    if (method === 'camera') {
      startScanning();
    } else if (method === 'face') {
      setIsFaceScanning(true);
    }
  };

  useEffect(() => {
    if (!isMethodControlled) return;
    handleScanMethodChange(controlledMethod);
  }, [isMethodControlled, controlledMethod]);

  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
        qrScannerRef.current = null;
      }
    };
  }, []);

  return (
    <Card className={className}>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-col sm:flex-row gap-2">
          {!isMethodControlled && (
            <Select value={scanMethod} onValueChange={handleScanMethodChange}>
              <SelectTrigger className="sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reader">Scan with reader</SelectItem>
                <SelectItem value="camera">Scan with camera</SelectItem>
                {faceIdEnabled && <SelectItem value="face">Face recognition</SelectItem>}
              </SelectContent>
            </Select>
          )}

          {!isPurposeControlled && (
            <Select
              value={selectedPurpose || undefined}
              onValueChange={(value) => {
                if (value) setSelectedPurpose(value);
              }}
            >
              <SelectTrigger className="sm:w-56">
                <SelectValue placeholder="Select scan purpose" />
              </SelectTrigger>
              <SelectContent>
                {purposes.map((purpose) => (
                  <SelectItem key={purpose.purpose} value={purpose.purpose}>
                    {purpose.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {!isPurposeControlled && selectedPurpose && (
          <QRScanStats eventId={eventId} purpose={selectedPurpose} />
        )}

        {isAutoSubmitting && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <p className="text-sm text-blue-800">Processing registration ID: {prefillRegistrationId}</p>
          </div>
        )}

        {scanMethod === 'reader' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleManualEntrySubmit(e);
            }}
            className="flex gap-2"
          >
            <Input
              ref={manualInputRef}
              id="manual-qr-input"
              value={manualQRCode}
              onChange={(e) => setManualQRCode(e.target.value)}
              placeholder="Scan QR code or enter registration ID"
              className="flex-1"
              autoFocus
            />
            <Button type="submit" disabled={!manualQRCode.trim() || isLoading}>
              Submit
            </Button>
          </form>
        )}

        {scanMethod === 'camera' && (
          <>
            {isScanning && (
              <div className="relative max-w-xl mx-auto">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full aspect-video max-h-[420px] bg-black rounded-lg object-cover"
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="absolute inset-0 border-2 border-white rounded-lg pointer-events-none">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white rounded-lg">
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white"></div>
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white"></div>
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white"></div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white"></div>
                  </div>
                </div>
              </div>
            )}
            {!isScanning && (
              <Button onClick={startScanning} variant="outline" className="w-full" disabled={isLoading}>
                Scan next
              </Button>
            )}
          </>
        )}

        {faceIdEnabled && scanMethod === 'face' && (
          <>
            <FaceCapture
              className="max-w-md mx-auto"
              mode="scan"
              autoCapture={true}
              isActive={isFaceScanning}
              onCapture={handleFaceCapture}
              onError={handleFaceError}
            />
            {!isFaceScanning && (
              <Button
                onClick={() => {
                  setIsFaceScanning(true);
                  setScanResult(null);
                }}
                variant="outline"
                className="w-full"
                disabled={isLoading}
              >
                Scan next
              </Button>
            )}
          </>
        )}

        {isLoading && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <p className="text-sm text-blue-800">
              {scanMethod === 'face' ? 'Processing face recognition...' : 'Fetching user details...'}
            </p>
          </div>
        )}

        {/* Scan Result */}
        {scanResult && (
          <div className={`p-4 border rounded-lg ${
            scanResult.scanStatus === 'success' 
              ? 'bg-green-50 border-green-200' 
              : scanResult.error || scanResult.scanStatus === 'error'
              ? 'bg-red-50 border-red-500'
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center space-x-2 mb-2">
              {scanResult.scanStatus === 'success' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <AlertCircle className={`w-4 h-4 ${scanResult.error || scanResult.scanStatus === 'error' ? 'text-red-600' : 'text-yellow-600'}`} />
              )}
              <p className={`text-sm font-medium ${
                scanResult.scanStatus === 'success' 
                  ? 'text-green-800' 
                  : scanResult.error || scanResult.scanStatus === 'error'
                  ? 'text-red-900'
                  : 'text-yellow-800'
              }`}>
                {scanResult.scanStatus === 'success' 
                  ? 'User Found' 
                  : scanResult.error || scanResult.scanStatus === 'error'
                  ? 'Scan Error'
                  : 'Scan Result'}
              </p>
            </div>
            
            {scanResult.scanStatus === 'success' ? (
              <>
                <p className="text-sm text-gray-700">
                  <strong>User ID:</strong> {scanResult.userId}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Name:</strong> {scanResult.formData?.['Name'] || 'N/A'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Category:</strong> {scanResult.formData?.['Category'] || 'General'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Email:</strong> {scanResult.formData?.['Email'] || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Purpose:</strong> {scanResult.purpose || purposes.find(p => p.purpose === selectedPurpose)?.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Scan Time:</strong> {formatDateTime(new Date())}
                </p>
                {previousScanMessage(scanResult) && (
                  <p className="mt-2 rounded-md bg-amber-100 px-2 py-1 text-sm font-medium text-amber-900">
                    {previousScanMessage(scanResult)}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-red-800 mb-1">
                  {scanResult.message || 'An error occurred during scanning'}
                </p>
                {previousScanMessage(scanResult) && (
                  <p className="text-sm text-red-800 mb-1">{previousScanMessage(scanResult)}</p>
                )}
                <p className="text-sm text-red-600">
                  <strong>Time:</strong> {formatDateTime(new Date())}
                </p>
              </>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  );
};

export default QRScanner;
